import { AlertCircle, CheckCircle2, CircleOff, History, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

import { useProductDiscontinuation, useRetryProductDiscontinuationSync, useSetProductDiscontinued } from '@/api/hooks'
import { ActionPopover } from '@/components/action-popover'
import { LoadingButton } from '@/components/loading-button'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getApiErrorMessage } from '@/lib/api-error'
import { parseUTCDate } from '@/lib/utils'

interface ProductDiscontinuationPanelProps {
  productId: string
  model: string
}

export function ProductDiscontinuationPanel({ productId, model }: ProductDiscontinuationPanelProps) {
  const stateQuery = useProductDiscontinuation(productId)
  const setDiscontinued = useSetProductDiscontinued(productId)
  const retrySync = useRetryProductDiscontinuationSync(productId)
  const state = stateQuery.data

  if (stateQuery.isLoading) {
    return <Skeleton className="h-24 w-full rounded-xl" />
  }

  if (!state) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        Product availability could not be loaded. Refresh the page before changing this product.
      </div>
    )
  }

  const pendingSync = state.shopify_linked && !state.shopify_synced
  const syncFailed = pendingSync && state.shopify_sync_status === 'failed'
  const latestEvent = state.events[0]
  const nextDiscontinued = !state.discontinued

  function confirmChange(close: () => void) {
    setDiscontinued.mutate(nextDiscontinued, {
      onSuccess: (nextState) => {
        close()
        if (nextState.shopify_linked && !nextState.shopify_synced) {
          toast.info('Product state saved. Shopify sync is still in progress.')
        } else {
          toast.success(nextState.discontinued ? 'Product marked discontinued' : 'Product restored')
        }
      },
      onError: (error) => toast.error(getApiErrorMessage(error, 'Product state could not be changed.')),
    })
  }

  function retry() {
    retrySync.mutate(undefined, {
      onSuccess: () => toast.info('Shopify sync queued again.'),
      onError: (error) => toast.error(getApiErrorMessage(error, 'Shopify sync could not be retried.')),
    })
  }

  return (
    <section aria-labelledby="availability-heading" className="rounded-xl border bg-card px-4 py-4 sm:px-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 space-y-2">
          <div aria-live="polite" className="flex flex-wrap items-center gap-2">
            <h2 id="availability-heading" className="text-sm font-semibold">
              Product availability
            </h2>
            <Badge variant={state.discontinued ? 'destructive' : 'secondary'}>
              {state.discontinued ? 'Discontinued' : 'Active'}
            </Badge>
            {!state.shopify_linked ? (
              <Badge variant="outline">Not linked to Shopify</Badge>
            ) : state.shopify_synced ? (
              <Badge variant="outline" className="gap-1 text-emerald-700">
                <CheckCircle2 aria-hidden="true" /> Shopify synced
              </Badge>
            ) : syncFailed ? (
              <Badge variant="destructive" className="gap-1">
                <AlertCircle aria-hidden="true" /> Shopify sync failed
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-amber-700">
                <RefreshCw aria-hidden="true" className="animate-spin" /> Shopify syncing
              </Badge>
            )}
          </div>

          <p className="max-w-3xl text-sm text-muted-foreground">
            {state.discontinued
              ? 'This product is excluded from storefront search, collections, and publication.'
              : 'This product is eligible for storefront workflows when its other catalog checks pass.'}
          </p>

          {latestEvent && (
            <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
              <History aria-hidden="true" className="size-3.5" />
              Last changed {parseUTCDate(latestEvent.created_at).toLocaleString()} by{' '}
              <span>{latestEvent.actor_name}</span>
              <span>({latestEvent.source.replaceAll('_', ' ')})</span>
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:justify-end">
          {syncFailed && (
            <LoadingButton variant="outline" loading={retrySync.isPending} loadingText="Retrying..." onClick={retry}>
              <RefreshCw aria-hidden="true" /> Retry Shopify sync
            </LoadingButton>
          )}
          <ActionPopover
            align="end"
            trigger={
              <Button
                variant={state.discontinued ? 'outline' : 'destructive'}
                disabled={state.discontinued && !state.restore_allowed}
              >
                <CircleOff aria-hidden="true" />
                {state.discontinued ? 'Restore product' : 'Mark discontinued'}
              </Button>
            }
            title={nextDiscontinued ? `Mark ${model} discontinued?` : `Restore ${model}?`}
            confirmLabel={nextDiscontinued ? 'Mark discontinued' : 'Restore product'}
            confirmVariant={nextDiscontinued ? 'destructive' : 'default'}
            pending={setDiscontinued.isPending}
            pendingLabel={nextDiscontinued ? 'Marking...' : 'Restoring...'}
            onConfirm={(_note, close) => confirmChange(close)}
          >
            <p className="text-xs text-muted-foreground">
              {nextDiscontinued
                ? 'The product will be removed from storefront eligibility immediately. Its Shopify discontinued tag will update in the background.'
                : 'The product will become eligible again only after Shopify removes the discontinued tag and all other catalog checks pass.'}
            </p>
          </ActionPopover>
        </div>
      </div>

      {syncFailed && (
        <div role="alert" className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
          <p className="text-sm font-medium text-destructive">Shopify is out of sync</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {state.shopify_sync_error ||
              'The last Shopify update failed. Retry the sync before leaving this unresolved.'}
          </p>
        </div>
      )}

      {state.discontinued && !state.restore_allowed && state.restore_blocked_reason && (
        <div role="note" className="mt-4 rounded-lg border border-amber-300/70 bg-amber-50 p-3 text-sm text-amber-950">
          {state.restore_blocked_reason}
        </div>
      )}
    </section>
  )
}
